import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  PowerSettingsNew as CloseIcon,
} from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import { fetchMyCompanies, setActiveCompany } from '../store/slices/companySlice';
import { logout } from '../store/slices/authSlice';

export default function CompanySettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((s) => s.auth);
  const { activeCompanyId, list: myCompanies } = useSelector((s) => s.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const isCompanyAdmin = activeCompany?.role === 'admin' || userInfo?.is_superadmin;

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState('');
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    setName(activeCompany?.name || '');
    setShortName(activeCompany?.short_name || '');
  }, [activeCompany?.name, activeCompany?.short_name]);

  if (!activeCompany) {
    return <Box><Alert severity="info">No active workspace.</Alert></Box>;
  }

  if (!isCompanyAdmin) {
    return (
      <Box>
        <Alert severity="error">
          You don't have permission to manage settings for this workspace.
        </Alert>
      </Box>
    );
  }

  const dirty = (name || '') !== (activeCompany.name || '') || (shortName || '') !== (activeCompany.short_name || '');

  const saveIdentity = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.patch('/api/companies-context/active', { name, short_name: shortName });
      await dispatch(fetchMyCompanies()).unwrap();
      setSuccess('Saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const closeAccount = async () => {
    setClosing(true); setError(null);
    try {
      await api.post('/api/companies-context/active/close');
      // Drop active company; if user has no other company, log out.
      dispatch(setActiveCompany(null));
      await dispatch(fetchMyCompanies()).unwrap();
      const remaining = (myCompanies || []).filter((c) => c.id !== activeCompany.id);
      if (remaining.length === 0 && !userInfo?.is_superadmin) {
        dispatch(logout());
        navigate('/login', { replace: true });
      } else {
        navigate('/pick-company', { replace: true });
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to close company');
      setClosing(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Workspace Settings" subtitle={activeCompany.name} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Identity</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The short name (abbreviation) is used in file naming, ISDCF DCP names, and as a workspace badge throughout the app.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Company name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Short name"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            size="small"
            placeholder="e.g. PB"
            inputProps={{ maxLength: 50 }}
            sx={{ width: 200 }}
            helperText="Up to 50 characters."
          />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || !dirty}
            onClick={saveIdentity}
            sx={{ mt: '4px' }}
          >
            Save
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Subscription</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Products currently enabled on your workspace. Contact HHMG to add or remove a product.
        </Typography>
        <Stack direction="row" spacing={1}>
          {activeCompany.has_press_review && <Chip label="Watermarks" color="primary" variant="outlined" />}
          {activeCompany.has_theatrical_dcp && <Chip label="Theatrical DCP" color="primary" variant="outlined" />}
          {!activeCompany.has_press_review && !activeCompany.has_theatrical_dcp && (
            <Typography variant="body2" color="text.secondary">No products enabled.</Typography>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <Typography variant="h6" color="error" sx={{ mb: 0.5 }}>Danger zone</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Closing your account deactivates this workspace. All members lose access immediately. Your assets are retained on storage and HHMG can reactivate the workspace on request.
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloseIcon />}
          onClick={() => { setCloseOpen(true); setCloseConfirm(''); }}
        >
          Close account
        </Button>
      </Paper>

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close {activeCompany.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This deactivates the workspace immediately. All members will be signed out on their next request. Type the company name below to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={activeCompany.name}
            value={closeConfirm}
            onChange={(e) => setCloseConfirm(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={closing || closeConfirm !== activeCompany.name}
            onClick={closeAccount}
          >
            {closing ? 'Closing...' : 'Close account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
